import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import NoData from 'components/Common/NoData';
import PartitionIcon from 'components/Common/PartitionIcon';
import PermissionService from 'components/Common/PermissionService';
import React from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import {
  END_DATE_PARAMETER,
  START_DATE_PARAMETER,
  isDateParametersAdded,
} from 'components/InternalAdmin/QuestionConfigure/QuestionParameters';
import { MONTH_BOUNDARY_TYPE, PARAMETER_TYPE } from 'pages/Flow/constants';
import { PARAM_SUFFIX } from 'components/CleanRooms/utils/partitionHelpers';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { productPerm } from 'utils/appConstants';


const INTERVAL_TYPE = {
  DAYS: 'DAYS',
  MONTHS: 'MONTHS',
};

const SUPPORTED_DATE_TYPES = ['DATE', 'TIMESTAMP'];

export const { PARTITION_PARAMETER, RUN_TIME_PARAMETER } = PARAMETER_TYPE;

// Flow-specific information
export const renderQNameForFlowParameter = (parameter) => (parameter?.questionName ? `${parameter?.questionName} - ` : null);

// Flow parameters use nodeID+name. Q-params use name only
export const getUniqueParamIdentifier = (parameter) => `${parameter?.nodeID ? `${parameter?.nodeID}-` : ''}${parameter?.name}`;

const renderIconForPartitionParameter = (parameter) => (
  parameter?.parameterType === PARTITION_PARAMETER ? <PartitionIcon /> : null
);

const addQualifierToDateParams = (params, qualifier) => {
  if (!params || params.length === 0) {
    return [];
  }

  const hasOneOrMoreDateParams = params.some(
    (param) => (
      // Run Time Parameters
      SUPPORTED_DATE_TYPES.includes(param.type)
      // Partition Parameters
      || SUPPORTED_DATE_TYPES.includes(param.dataType)
    ),
  );
  if (!hasOneOrMoreDateParams) {
    return params;
  }

  return params.map((param) => ({ ...param, qualifier }));
};

const getInitialIntervalType = ({ START_DATE = {}, END_DATE = {} } = {}) => {
  if (!START_DATE.qualifier || !END_DATE.qualifier) {
    return INTERVAL_TYPE.DAYS;
  }
  return START_DATE.qualifier;
};

const getInitialPartitionIntervalType = (parameters = {}) => {
  // Find any partition parameter with a qualifier to determine interval type
  const partitionParam = Object.values(parameters).find(
    param => param.parameterType === PARTITION_PARAMETER && param.qualifier,
  );

  return partitionParam?.qualifier || INTERVAL_TYPE.DAYS;
};

const getNegativeValue = (value) => (String(value ?? '').trim() === '0' ? '0' : value * -1 || '');

const DateParameter = ({
  parameter, details, updateDetails,
}) => {
  const parameterIdentifier = getUniqueParamIdentifier(parameter);
  return (
    <>
      <Grid item xs={5}>
        <Box display='flex' alignItems='center'>
          <Typography variant='subtitle2' sx={{ overflowWrap: 'anywhere' }}>
            {renderQNameForFlowParameter(parameter)}
            {parameter?.displayName || parameter.name}
          </Typography>
          {renderIconForPartitionParameter(parameter)}
        </Box>
      </Grid>
      <Grid item xs={7}>
        <Box display='flex'>
          <Typography variant='subtitle2'>
            {details.parameters[parameterIdentifier]?.isStatic ? ' ' : '# Days before Run Date'}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={5}>
        <Box display='flex' alignItems='center' height='100%'>
          <ToggleButtonGroup
            color='primary'
            value={details.parameters[parameterIdentifier]?.isStatic || false}
            exclusive
            onChange={(e, value) => updateDetails(prevDetails => ({
              ...prevDetails,
              parameters: {
                ...prevDetails.parameters,
                [parameterIdentifier]: {
                  ...prevDetails.parameters[parameterIdentifier],
                  isStatic: value,
                  value: null,
                },
              },
            }))}
            aria-label='Platform'
            size='small'
          >
            <ToggleButton value={false} color='primary'>Relative Date</ToggleButton>
            {/* eslint-disable-next-line react/jsx-boolean-value */}
            <ToggleButton value={true}>Fixed Date</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>
      <Grid item xs={5}>
        {details.parameters[parameterIdentifier]?.isStatic ? (
          <TextField
            fullWidth
            helperText={parameter?.description || ''}
            placeholder={parameter?.exampleValue || ''}
            margin='dense'
            onChange={e => updateDetails({
              ...details,
              parameters: {
                ...details.parameters,
                [parameterIdentifier]: {
                  ...details.parameters[parameterIdentifier],
                  value: e.target.value,
                  name: parameter.name,
                  type: parameter?.type || parameter?.dataType,
                  qualifier: parameter?.qualifier,
                  parameterType: parameter?.parameterType ?? '',
                  ...(parameter?.nodeID ? { nodeID: parameter?.nodeID } : {}),
                },
              },
            })}
            type={parameter.dataType === 'TIMESTAMP' ? 'datetime-local' : 'date'}
            inputProps={{
              step: 1,
            }}
            value={details.parameters[parameterIdentifier]?.value || ''}
            variant='outlined'
            required
            size='small'
          />
        ) : (
          <TextField
            fullWidth
            helperText={parameter?.description || ''}
            placeholder={parameter?.exampleValue || ''}
            margin='dense'
            onChange={e => updateDetails({
              ...details,
              parameters: {
                ...details.parameters,
                [parameterIdentifier]: {
                  ...details.parameters[parameterIdentifier],
                  value: e.target.value ? String(e.target.value * -1) : e.target.value,
                  name: parameter.name,
                  type: parameter?.type || parameter?.dataType,
                  qualifier: parameter?.qualifier,
                  parameterType: parameter?.parameterType ?? '',
                  ...(parameter?.nodeID ? { nodeID: parameter?.nodeID } : {}),
                },
              },
            })}
            required
            value={getNegativeValue(details.parameters[parameterIdentifier]?.value)}
            variant='outlined'
            type='number'
            size='small'
            inputProps={{
              min: 0,
            }}
          />
        )}
      </Grid>
      <Grid item xs={2} />
    </>
  );
};

const MonthsParameters = ({
  parameter, details, updateDetails,
}) => {
  const parameterIdentifier = getUniqueParamIdentifier(parameter);

  const handleValueChange = (e) => {
    const newValue = e.target.value ? String(e.target.value * -1) : e.target.value;
    updateDetails({
      ...details,
      parameters: {
        ...details.parameters,
        [parameterIdentifier]: {
          ...details.parameters[parameterIdentifier],
          value: newValue,
          name: parameter.name,
          type: parameter?.type || parameter?.dataType,
          qualifier: parameter?.qualifier,
          parameterType: parameter?.parameterType ?? '',
          // Preserve boundaryType or default to MONTH_START if not set
          boundaryType: details.parameters[parameterIdentifier]?.boundaryType
            || MONTH_BOUNDARY_TYPE.MONTH_START,
          ...(parameter?.nodeID ? { nodeID: parameter?.nodeID } : {}),
        },
      },
    });
  };

  const handleBoundaryTypeChange = (e, boundaryType) => {
    if (boundaryType === null) return;

    updateDetails({
      ...details,
      parameters: {
        ...details.parameters,
        [parameterIdentifier]: {
          ...details.parameters[parameterIdentifier],
          boundaryType,
          name: parameter.name,
          type: parameter?.type || parameter?.dataType,
          qualifier: parameter?.qualifier,
          parameterType: parameter?.parameterType ?? '',
          // Preserve the value when changing boundary type
          value: details.parameters[parameterIdentifier]?.value || '',
          ...(parameter?.nodeID ? { nodeID: parameter?.nodeID } : {}),
        },
      },
    });
  };

  const getPreviewDate = () => {
    const value = details.parameters[parameterIdentifier]?.value;
    const boundaryType = details.parameters[parameterIdentifier]?.boundaryType
      || MONTH_BOUNDARY_TYPE.MONTH_START;

    if (!value) return 'Enter months value';

    const monthsBack = parseInt(value, 10);

    // Validate that the input is a valid number
    if (Number.isNaN(monthsBack)) {
      return 'Invalid value - please enter a number';
    }

    const runDate = moment();

    let previewDate;
    let description;

    if (boundaryType === MONTH_BOUNDARY_TYPE.MONTH_START) {
      previewDate = runDate.clone().add(monthsBack, 'month').startOf('month');
      description = 'First day';
    }
    else {
      // MONTH_END: Match backend logic - go to start of next month, then back 1 day
      // Backend: AddDate(0, monthsBack+1, -dayOfMonth) effectively gets last day of target month
      previewDate = runDate.clone().add(monthsBack + 1, 'month').startOf('month').subtract(1, 'day');
      description = 'Last day';
    }

    const absMonths = Math.abs(monthsBack);
    let monthText;
    if (absMonths === 0) {
      monthText = 'current month';
    }
    else if (absMonths === 1) {
      monthText = '1 month ago';
    }
    else {
      monthText = `${absMonths} months ago`;
    }

    return `${previewDate.format('YYYY-MM-DD')} (${description} of ${monthText})`;
  };

  return (
    <>
      <Grid item xs={12}>
        <Box display='flex' alignItems='center'>
          <Typography variant='subtitle2'>
            {renderQNameForFlowParameter(parameter)}
            {parameter?.displayName || parameter.name}
          </Typography>
          {renderIconForPartitionParameter(parameter)}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant='caption' color='textSecondary' gutterBottom>
          Month Boundary Type
        </Typography>
        <Box mt={0.5}>
          <ToggleButtonGroup
            color='primary'
            value={
              details.parameters[parameterIdentifier]?.boundaryType
              || MONTH_BOUNDARY_TYPE.MONTH_START
            }
            exclusive
            onChange={handleBoundaryTypeChange}
            size='small'
            fullWidth
          >
            <ToggleButton value={MONTH_BOUNDARY_TYPE.MONTH_START}>
              Month Start (1st)
            </ToggleButton>
            <ToggleButton value={MONTH_BOUNDARY_TYPE.MONTH_END}>
              Month End (Last day)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant='caption' color='textSecondary' gutterBottom>
          # of previous calendar months
        </Typography>
        <TextField
          variant='outlined'
          size='small'
          type='number'
          required
          onChange={handleValueChange}
          value={getNegativeValue(details.parameters[parameterIdentifier]?.value)}
          fullWidth
          inputProps={{ min: 0, max: 24 }}
          helperText='0 = current month, 1 = previous month, etc.'
        />
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant='caption' color='textSecondary'>
            Preview:
          </Typography>
          <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
            {getPreviewDate()}
          </Typography>
        </Box>
      </Grid>
    </>
  );
};

const Parameters = ({
  details, updateDetails, entityDetails, crqParameters,
}) => {
  const [entity, updateEntity] = React.useState(entityDetails);

  // Separate interval types for runtime and partition parameters
  const [runtimeIntervalType, setRuntimeIntervalType] = React.useState(
    () => getInitialIntervalType(details.parameters),
  );
  const [partitionIntervalType, setPartitionIntervalType] = React.useState(
    () => getInitialPartitionIntervalType(details.parameters),
  );

  // Check if relative months feature is enabled via feature flipper
  const isRelativeMonthsEnabled = PermissionService.isProductEnabled([
    productPerm.EnableRelativeMonthsForScheduler,
  ]);

  // Shared handler factory for interval changes
  const createIntervalChangeHandler = (parameterTypeFilter, setIntervalType) => (e, interval) => {
    if (interval === null) {
      return;
    }

    const { parameters } = details;
    const updatedParams = {};

    Object.entries(parameters).forEach(([key, param]) => {
      if (param.type === 'DATE' && parameterTypeFilter(param)) {
        updatedParams[key] = {
          ...param,
          qualifier: interval,
          value: '', // Reset value when switching interval types
          // Set default boundaryType when switching to MONTHS
          ...(interval === INTERVAL_TYPE.MONTHS && {
            boundaryType: param.boundaryType || MONTH_BOUNDARY_TYPE.MONTH_START,
          }),
        };
      }
    });

    // Handle END_DATE for runtime parameters (parameterType is empty string for runtime)
    if (parameterTypeFilter({ parameterType: '' }) && !Object.hasOwn(updatedParams, 'END_DATE')) {
      updatedParams.END_DATE = {
        name: END_DATE_PARAMETER.name,
        type: END_DATE_PARAMETER.type,
        qualifier: interval,
        value: '',
        parameterType: '',
      };
    }

    updateDetails({
      ...details,
      parameters: {
        ...parameters,
        ...updatedParams,
      },
    });
    setIntervalType(interval);
  };

  // Handler for runtime parameter interval changes
  const handleRuntimeIntervalChange = createIntervalChangeHandler(
    (param) => param.parameterType !== PARTITION_PARAMETER,
    setRuntimeIntervalType,
  );

  // Handler for partition parameter interval changes
  const handlePartitionIntervalChange = createIntervalChangeHandler(
    (param) => param.parameterType === PARTITION_PARAMETER,
    setPartitionIntervalType,
  );

  const derivedRunParameters = React.useMemo(() => {
    let processedRunParams = entity?.parameters || [];

    // Add a 'qualifier' to all date-related parameters based on the RUNTIME interval type
    processedRunParams = addQualifierToDateParams(processedRunParams, runtimeIntervalType);

    // If the entity is configured to have start/end dates, add them if they are missing.
    if (entity.hasDateParameters && !isDateParametersAdded(processedRunParams)) {
      const defaultDateParameters = [{
        ...START_DATE_PARAMETER,
        qualifier: runtimeIntervalType,
      },
      {
        ...END_DATE_PARAMETER,
        qualifier: runtimeIntervalType,
      }];

      processedRunParams = [
        ...defaultDateParameters,
        ...processedRunParams,
      ];
    }

    return processedRunParams;
  }, [entity, runtimeIntervalType]);

  const derivedPartitionParameters = React.useMemo(() => {
    // Add a 'qualifier' to all date-related parameters based on the PARTITION interval type
    const paramsWithQualifier = addQualifierToDateParams(crqParameters, partitionIntervalType);
    // Filter out _rolling parameters (used only for materialization, not scheduling)
    return paramsWithQualifier.filter(param => !param.name.endsWith(PARAM_SUFFIX.ROLLING));
  }, [crqParameters, partitionIntervalType]);

  React.useEffect(() => {
    if (entityDetails) {
      updateEntity(entityDetails);
    }
  }, [entityDetails]);

  // Initialize interval types when loading a scheduled run for editing
  // We intentionally only depend on details.ID (not details.parameters) to avoid
  // resetting interval types when the user edits parameter values
  React.useEffect(() => {
    if (details.ID) {
      const initialRuntimeInterval = getInitialIntervalType(details.parameters);
      const initialPartitionInterval = getInitialPartitionIntervalType(details.parameters);
      setRuntimeIntervalType(initialRuntimeInterval);
      setPartitionIntervalType(initialPartitionInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.ID]);

  // Initialize boundaryType for parameters with MONTHS qualifier but empty boundaryType
  React.useEffect(() => {
    const hasMonthsInterval = runtimeIntervalType === INTERVAL_TYPE.MONTHS
      || partitionIntervalType === INTERVAL_TYPE.MONTHS;
    if (hasMonthsInterval && details.parameters) {
      const needsUpdate = Object.entries(details.parameters).some(
        ([, param]) => param.qualifier === INTERVAL_TYPE.MONTHS
          && param.type === 'DATE'
          && !param.boundaryType,
      );

      if (needsUpdate) {
        const updatedParams = {};
        Object.entries(details.parameters).forEach(([key, param]) => {
          if (param.qualifier === INTERVAL_TYPE.MONTHS
            && param.type === 'DATE'
            && !param.boundaryType) {
            updatedParams[key] = {
              ...param,
              boundaryType: MONTH_BOUNDARY_TYPE.MONTH_START,
            };
          }
        });

        if (Object.keys(updatedParams).length > 0) {
          updateDetails({
            ...details,
            parameters: {
              ...details.parameters,
              ...updatedParams,
            },
          });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeIntervalType, partitionIntervalType, details.ID]);

  return (
    <>
      <Grid item xs={12}>
        <Typography variant='subtitle2'>Runtime Parameters</Typography>
      </Grid>
      {derivedRunParameters.length === 0
        ? (
          <Grid item xs={12}>
            <NoData text='No Runtime Parameters for this Question.' />
          </Grid>
        ) : (
          null
        )}
      {derivedRunParameters.length > 0 && entity?.hasDateParameters && isRelativeMonthsEnabled
        ? (
          <>
            <Grid item xs={12}>
              <Typography variant='subtitle2'>Runtime Interval Type</Typography>
            </Grid>
            <Grid item xs={12}>
              <ToggleButtonGroup
                exclusive
                color='primary'
                size='small'
                aria-label='Runtime Interval'
                value={runtimeIntervalType}
                onChange={handleRuntimeIntervalChange}
              >
                <ToggleButton value={INTERVAL_TYPE.DAYS}>Days</ToggleButton>
                <ToggleButton value={INTERVAL_TYPE.MONTHS}>Months</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </>
        ) : null}
      {orderBy(derivedRunParameters, ['index'], ['asc']).map(parameter => {
        const parameterIdentifier = getUniqueParamIdentifier(parameter);
        if (isRelativeMonthsEnabled
          && runtimeIntervalType === INTERVAL_TYPE.MONTHS
          && String(parameter.type).toLowerCase() === 'date') {
          // Show MonthsParameters when feature enabled and runtime interval is MONTHS
          return (
            <MonthsParameters
              details={details}
              updateDetails={updateDetails}
              parameter={parameter}
              key={parameterIdentifier}
            />
          );
        }
        if (String(parameter.type).toLowerCase() === 'date') {
          return (
            <DateParameter
              details={details}
              updateDetails={updateDetails}
              parameter={parameter}
              key={parameterIdentifier}
            />
          );
        }
        return (
          <React.Fragment key={parameterIdentifier}>
            <Grid item xs={10}>
              <Box display='flex' alignItems='center'>
                <Typography variant='subtitle2'>
                  {renderQNameForFlowParameter(parameter)}
                  {parameter.displayName || parameter.name}
                </Typography>
                {renderIconForPartitionParameter(parameter)}
              </Box>
              <TextField
                fullWidth
                helperText={parameter?.description || ''}
                placeholder={parameter?.exampleValue || ''}
                margin='dense'
                onChange={e => updateDetails({
                  ...details,
                  parameters: {
                    ...details.parameters,
                    [parameterIdentifier]: {
                      ...details.parameters[parameterIdentifier],
                      value: e.target.value,
                      name: parameter.name,
                      type: parameter.type,
                      parameterType: parameter.parameterType ?? '',
                      ...(
                        parameter?.nodeID ? { nodeID: parameter?.nodeID } : {}
                      ),
                    },
                  },
                })}
                required
                value={details.parameters[parameterIdentifier]?.value || ''}
                variant='outlined'
                size='small'
              />
            </Grid>
            <Grid item xs={2} />
          </React.Fragment>
        );
      })}
      {derivedPartitionParameters?.length > 0
        && (
          <>
            <Grid item xs={12}>
              <Box display='flex' alignItems='center'>
                <Typography variant='subtitle2'>Partition Parameters</Typography>
                <PartitionIcon />
              </Box>
            </Grid>
            {isRelativeMonthsEnabled
              && derivedPartitionParameters.some(p => SUPPORTED_DATE_TYPES.includes(p.dataType))
              ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant='subtitle2'>Partition Interval Type</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ToggleButtonGroup
                      exclusive
                      color='primary'
                      size='small'
                      aria-label='Partition Interval'
                      value={partitionIntervalType}
                      onChange={handlePartitionIntervalChange}
                    >
                      <ToggleButton value={INTERVAL_TYPE.DAYS}>Days</ToggleButton>
                      <ToggleButton value={INTERVAL_TYPE.MONTHS}>Months</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </>
              ) : null}
            {orderBy(derivedPartitionParameters, ['index'], ['asc']).map(parameter => {
              const parameterIdentifier = getUniqueParamIdentifier(parameter);

              if (SUPPORTED_DATE_TYPES.includes(parameter.dataType)) {
                // Check if feature enabled and MONTHS interval for PARTITION parameters
                if (isRelativeMonthsEnabled
                  && partitionIntervalType === INTERVAL_TYPE.MONTHS) {
                  return (
                    <MonthsParameters
                      details={details}
                      updateDetails={updateDetails}
                      parameter={parameter}
                      key={parameterIdentifier}
                    />
                  );
                }

                // Otherwise use regular DateParameter (for DAYS)
                return (
                  <DateParameter
                    details={details}
                    updateDetails={updateDetails}
                    parameter={parameter}
                    key={parameterIdentifier}
                    parameterType={PARTITION_PARAMETER}
                  />
                );
              }
              return (
                <>
                  <Grid item xs={10} key={parameter.name}>
                    <Typography variant='subtitle2'>
                      {parameter.name}
                    </Typography>
                    <TextField
                      fullWidth
                      margin='dense'
                      onChange={e => updateDetails({
                        ...details,
                        parameters: {
                          ...details.parameters,
                          [parameter.name]: {
                            ...details.parameters[parameter.name],
                            value: e.target.value,
                            name: parameter.name,
                            type: parameter.dataType,
                            parameterType: PARTITION_PARAMETER,
                          },
                        },
                      })}
                      required
                      value={details.parameters[parameter.name]?.value || ''}
                      variant='outlined'
                      size='small'
                    />
                  </Grid>
                  <Grid item xs={2} />
                </>
              );
            })}
          </>
        )}
    </>
  );
};

export default Parameters;

